import { useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, X, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatAUD } from "@/context/CartContext";
import PayPalCheckout from "@/components/PayPalCheckout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_SHIPPING = 150;

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const shopifyMode = items.length > 0 && items.every((i) => i.variant_id);

  const checkout = async () => {
    setLoading(true);
    try {
      if (shopifyMode) {
        const { data } = await axios.post(`${API}/shopify/checkout`, {
          items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.qty })),
        });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await axios.post(`${API}/payments/cart-checkout`, {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty, color: i.color })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout could not be started. Please try again.");
      setLoading(false);
    }
  };

  const remaining = Math.max(0, FREE_SHIPPING - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md bg-marble border-l border-gold/30 p-0 flex flex-col"
      >
        <SheetHeader className="px-8 pt-8 pb-6 border-b border-taupe/30">
          <SheetTitle className="font-serif-lux text-3xl text-charcoal font-semibold">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div data-testid="cart-empty-state" className="flex-1 flex flex-col items-center justify-center gap-4 px-8">
            <ShoppingBag size={40} strokeWidth={1} className="text-taupe" />
            <p className="text-charcoal/50 font-light">Your cart is empty.</p>
            <button
              data-testid="cart-continue-shopping"
              onClick={() => setOpen(false)}
              className="mt-2 text-xs tracking-[0.25em] uppercase text-gold gold-underline"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
              {items.map((item) => {
                const key = item.id + (item.color ? `::${item.color}` : "");
                const colorHex = item.colors && item.color
                  ? (item.colors.find((c) => c.name === item.color) || {}).hex
                  : null;
                return (
                  <div key={key} data-testid={`cart-item-${key}`} className="flex gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-sm border border-taupe/30 bg-white"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="font-serif-lux text-lg text-charcoal leading-snug">{item.name}</h4>
                          {item.color && (
                            <p data-testid={`cart-color-${key}`} className="text-[11px] text-charcoal/50 mt-0.5 flex items-center gap-1.5">
                              {colorHex && (
                                <span className="inline-block w-3 h-3 rounded-full border border-taupe/50" style={{ backgroundColor: colorHex }} />
                              )}
                              {item.color}
                            </p>
                          )}
                        </div>
                        <button
                          data-testid={`cart-remove-${key}`}
                          onClick={() => remove(item.id, item.color)}
                          aria-label={`Remove ${item.name}`}
                          className="text-charcoal/40 hover:text-charcoal transition-colors self-start"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-sm text-charcoal/50 mt-0.5">{formatAUD(item.price)}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center border border-taupe/50 rounded-full">
                          <button
                            data-testid={`cart-qty-minus-${key}`}
                            onClick={() => setQty(item.id, item.color, item.qty - 1)}
                            className="w-8 h-8 flex items-center justify-center text-charcoal/60 hover:text-charcoal"
                          >
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-qty-${key}`} className="w-6 text-center text-sm">{item.qty}</span>
                          <button
                            data-testid={`cart-qty-plus-${key}`}
                            onClick={() => setQty(item.id, item.color, item.qty + 1)}
                            className="w-8 h-8 flex items-center justify-center text-charcoal/60 hover:text-charcoal"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <span className="text-sm font-medium text-charcoal ml-auto">
                          {formatAUD(item.price * item.qty)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-8 py-6 border-t border-taupe/30 bg-white/60 backdrop-blur-xl">
              <div
                data-testid="promo-banner"
                className="flex items-center justify-between gap-3 border border-gold/40 bg-gold/10 rounded-sm px-4 py-3 mb-5"
              >
                <span className="text-[11px] tracking-[0.2em] uppercase text-gold-dark font-medium">
                  Launch offer — 10% off
                </span>
                <span className="text-xs font-semibold text-charcoal tracking-widest border border-dashed border-gold/60 rounded-full px-3 py-1">
                  WELCOME10
                </span>
              </div>
              {shopifyMode && (
                <p className="text-[10px] text-charcoal/50 mb-4 tracking-wide">Enter the code at the Shopify checkout page</p>
              )}
              {remaining > 0 ? (
                <p data-testid="free-shipping-hint" className="text-xs text-charcoal/60 mb-3">
                  Add <span className="text-gold-dark font-medium">{formatAUD(remaining)}</span> more for free shipping
                </p>
              ) : (
                <p data-testid="free-shipping-unlocked" className="text-xs text-gold-dark font-medium mb-3">
                  You've unlocked free shipping
                </p>
              )}
              <div className="h-1 bg-taupe/30 rounded-full mb-6 overflow-hidden">
                <div
                  className="h-full bg-gold rounded-full transition-[width] duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between items-baseline mb-5">
                <span className="text-xs tracking-[0.25em] uppercase text-charcoal/60">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-serif-lux text-2xl text-charcoal">
                  {formatAUD(subtotal)}
                </span>
              </div>
              <button
                data-testid="checkout-button"
                onClick={checkout}
                disabled={loading}
                className="w-full py-4 bg-gold text-charcoal text-xs tracking-[0.3em] uppercase font-semibold rounded-full hover:bg-gold-dark transition-colors duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Lock size={14} strokeWidth={2} />
                {loading ? "Redirecting…" : shopifyMode ? "Checkout with Shopify" : "Secure Checkout"}
              </button>
              {!shopifyMode && <PayPalCheckout />}
              {!shopifyMode && (
                <p className="text-[10px] text-center text-charcoal/40 mt-3 tracking-wider">
                  Test mode — use card 4242 4242 4242 4242
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
